/**
 * Periodic Background Sync wrapper.
 *
 * Pre-fetches the student's study summary in the background every ~12 hours
 * (Chrome-imposed minimum), so opening the app on a poor connection still
 * shows fresh dashboard / Smart Review / analytics data.
 *
 * Browser support: Chrome/Edge on Android (and Chrome desktop) — only inside
 * an installed PWA, with explicit user permission. iOS Safari and Firefox
 * return `supported=false` and the toggle UI hides itself.
 */
import { useEffect, useState } from "react";

const TAG = "pm-prefetch";
const TWELVE_HOURS_MS = 12 * 60 * 60 * 1000;

export function isPeriodicSyncSupported() {
  if (typeof navigator === "undefined") return false;
  if (!("serviceWorker" in navigator)) return false;
  // Feature-detect on the ServiceWorkerRegistration prototype if available.
  return "periodicSync" in (window.ServiceWorkerRegistration?.prototype || {});
}

async function getRegistration() {
  if (!("serviceWorker" in navigator)) return null;
  try { return await navigator.serviceWorker.ready; } catch { return null; }
}

export async function getPermissionState() {
  if (!isPeriodicSyncSupported() || !navigator.permissions) return "unsupported";
  try {
    const status = await navigator.permissions.query({ name: "periodic-background-sync" });
    return status.state; // "granted" | "denied" | "prompt"
  } catch { return "unsupported"; }
}

export async function isRegistered() {
  const reg = await getRegistration();
  if (!reg || !("periodicSync" in reg)) return false;
  try {
    const tags = await reg.periodicSync.getTags();
    return tags.includes(TAG);
  } catch { return false; }
}

/**
 * Persists the auth token to IndexedDB so the SW can authorize prefetch
 * requests without a live page. Called on login/logout.
 */
export async function syncTokenToSW(token) {
  if (!("indexedDB" in window)) return;
  await new Promise((resolve) => {
    const req = indexedDB.open("prepmasters-config", 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains("kv")) db.createObjectStore("kv");
    };
    req.onsuccess = () => {
      const db = req.result;
      const tx = db.transaction("kv", "readwrite");
      if (token) tx.objectStore("kv").put(token, "pm_token");
      else tx.objectStore("kv").delete("pm_token");
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    };
    req.onerror = () => resolve();
  });
}

export async function enablePrefetch() {
  const reg = await getRegistration();
  if (!reg || !("periodicSync" in reg)) throw new Error("Periodic Sync unavailable");
  // Push the current token so the SW can authorize prefetch calls
  await syncTokenToSW(localStorage.getItem("pm_token") || "");
  try {
    await reg.periodicSync.register(TAG, { minInterval: TWELVE_HOURS_MS });
  } catch (e) {
    throw new Error(e?.message || "Could not register periodic sync");
  }
  // Trigger an immediate one-shot prefetch via SW message so the user sees
  // the benefit right away (the periodic event itself fires ~12 h later).
  try {
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: "pm-prefetch-now" });
    }
  } catch { /* noop */ }
}

export async function disablePrefetch() {
  const reg = await getRegistration();
  if (!reg || !("periodicSync" in reg)) return;
  try { await reg.periodicSync.unregister(TAG); } catch { /* noop */ }
}

/**
 * React hook for the StudentAccount toggle.
 *   { supported, permission, enabled, busy, lastError, toggle }
 */
export function usePeriodicSync() {
  const [supported] = useState(isPeriodicSyncSupported);
  const [permission, setPermission] = useState("prompt");
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState(null);

  const refresh = async () => {
    setPermission(await getPermissionState());
    setEnabled(await isRegistered());
  };

  useEffect(() => {
    if (!supported) return;
    refresh();
  }, [supported]);

  const toggle = async (next) => {
    setBusy(true);
    setLastError(null);
    try {
      if (next) await enablePrefetch();
      else await disablePrefetch();
      await refresh();
    } catch (e) {
      setLastError(e?.message || "Could not update setting");
    } finally { setBusy(false); }
  };

  return { supported, permission, enabled, busy, lastError, toggle };
}
